import { NavLink } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import {
  ButtonSecondary,
  ButtonPseudo,
  SidePanelRow,
  SidePanelLoader,
  IconRefresh,
  Select,
  ButtonSize,
  Autocomplete,
  IconChangeLoClock,
} from '@ui/src';
import { useAppSelector } from '@store';
import { MarginAmount } from '../../hoc/margins/types';
import { MarginPanelContentProps } from './types';
import useGetGainAutocompleteList from '../../store/dictionaries/helpers/use-gain-accounts-autocomplete';
import useGetLossesAutocompleteList from '../../store/dictionaries/helpers/use-losses-accounts-autocomplete';
import MarginAmountItem from './margin-amount-item';

import cn from './styles.module.css';

const MarginPanelContent = ({
  control,
  isLoading,
  errors,
  isDefaultPanel,
  onResetMargins,
  isEditable,
  amounts,
  setValue,
  isCustom,
  marginId,
  setAmounts,
  marginFeeError,
  auditLogAvailable,
  restoreInProgress,
  currencyFromId,
}: MarginPanelContentProps): React.JSX.Element => {
  const { accounts: gainAcounts, onSearch: onGainSearch } =
    useGetGainAutocompleteList();
  const { accounts: lossesAcounts, onSearch: onLossesSearch } =
    useGetLossesAutocompleteList();

  const { currencies, tariffs, rateSource } = useAppSelector(
    (state) => state.exchangeDictionaries,
  );

  let content: React.JSX.Element;

  if (isLoading) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        {!isDefaultPanel && (
          <>
            <div className={cn.blockWrapper}>
              <div className={cn.row}>
                <SidePanelRow caption="Currency From">
                  <Controller
                    control={control}
                    name="currencyFromId"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <Select
                        placeholder="Enter currency from..."
                        name={name}
                        value={value}
                        onChange={onChange}
                        selectWrapperClassName={cn.currencySelect}
                        list={currencies?.map((item) => ({
                          name: item.name,
                          value: item.id,
                        }))}
                        disabled
                        fullWidth
                        error={errors?.currencyFromId?.message}
                      />
                    )}
                  />
                </SidePanelRow>
                <SidePanelRow caption="Currency To">
                  <Controller
                    control={control}
                    name="currencyToId"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <Select
                        placeholder="Enter currency to..."
                        name={name}
                        value={value}
                        selectWrapperClassName={cn.currencySelect}
                        onChange={onChange}
                        list={currencies?.map((item) => ({
                          name: item.name,
                          value: item.id,
                        }))}
                        disabled
                        fullWidth
                        error={errors?.currencyFromId?.message}
                      />
                    )}
                  />
                </SidePanelRow>
              </div>
              <SidePanelRow caption="Tariff">
                <Controller
                  control={control}
                  name="tariffId"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      onChange={onChange}
                      value={value}
                      list={tariffs}
                      disabled={!isEditable}
                      fullWidth
                    />
                  )}
                />
              </SidePanelRow>
            </div>
            <div className={cn.marginTitle}>Set Rate type</div>
            <SidePanelRow>
              <Controller
                control={control}
                defaultValue=""
                name="rateSourceId"
                render={({ field: { onChange, value, name } }) => (
                  <Select
                    name={name}
                    onChange={onChange}
                    value={value}
                    list={rateSource?.map((item) => ({
                      name: item.name,
                      value: item.id,
                    }))}
                    disabled={!isEditable}
                    fullWidth
                  />
                )}
              />
            </SidePanelRow>
          </>
        )}

        <div className={cn.marginTopWrapper}>
          {isDefaultPanel ? (
            <div className={cn.marginTitle}>Set FX margin for all pairs</div>
          ) : (
            <>
              <div className={cn.marginTitle}>Set FX margin</div>
              {isEditable && (
                <ButtonPseudo
                  text="Restore defaults"
                  icon={
                    <IconRefresh
                      className={restoreInProgress ? cn.rotate : ''}
                    />
                  }
                  size={ButtonSize.SMALL}
                  bold
                  onClick={() => onResetMargins && onResetMargins(true)}
                  disabled={!isCustom || restoreInProgress}
                />
              )}
            </>
          )}
        </div>
        <SidePanelRow caption="Target account - Gain">
          <Controller
            control={control}
            name="targetAccountGainsId"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <Autocomplete
                name={name}
                placeholder="Select account..."
                value={value}
                onChange={onChange}
                list={gainAcounts}
                onSearch={onGainSearch}
                disabled={!isEditable}
                fullWidth
                withSuffix
              />
            )}
          />
        </SidePanelRow>
        <SidePanelRow caption="Target account - Losses">
          <Controller
            control={control}
            name="targetAccountLossesId"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <Autocomplete
                name={name}
                placeholder="Select account..."
                value={value || ''}
                onChange={onChange}
                list={lossesAcounts}
                onSearch={onLossesSearch}
                disabled={!isEditable}
                fullWidth
                withSuffix
              />
            )}
          />
        </SidePanelRow>
        <div>
          <div className={cn.feeRanges}>
            {amounts?.map((item, index) => (
              <MarginAmountItem
                key={item?.id}
                amount={item}
                index={index}
                amounts={amounts}
                setAmounts={setAmounts}
                isEditable={isEditable}
                setValue={setValue}
                isCustom={isCustom}
                marginId={marginId}
                marginFeeError={marginFeeError}
                currencies={currencies}
                currencyFromId={currencyFromId}
              />
            ))}
          </div>
          <Controller
            control={control}
            name="amounts"
            render={({ field: { name } }) => (
              <input type="hidden" name={name} />
            )}
          />
          {isEditable && (
            <ButtonSecondary
              className={cn.feeRangeAdd}
              text="Add price range"
              onClick={() => {
                let nextAmounts = ([] as MarginAmount[]).concat(
                  amounts as MarginAmount[],
                );

                if (!amounts?.length) {
                  nextAmounts = [];
                }

                nextAmounts.push({
                  fromAmount: 0,
                  feePercent: 0,
                  id: `amount-${(amounts?.length as number) + 1}`,
                });

                setAmounts(nextAmounts);
              }}
            />
          )}
        </div>
        {auditLogAvailable && (
          <NavLink
            to={`/administration/change-logs?entityType=${
              isDefaultPanel ? 'DEFAULT_MARGIN' : 'MARGIN'
            }${marginId !== 'all' ? `&entityId=${marginId}` : ''}`}
            target="_blank"
            className={cn.changeLogLink}
          >
            <IconChangeLoClock />
            Change Log
          </NavLink>
        )}
      </>
    );
  }

  return <div className={cn.sidepanel}>{content}</div>;
};

export default MarginPanelContent;
